import React, { useCallback, useMemo, useState } from "react";
import {
  ConnectAccountManagement,
  ConnectAccountOnboarding,
  ConnectComponentsProvider,
  ConnectNotificationBanner,
} from "@stripe/react-connect-js";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import { refreshCardPaymentsSession } from "../../../utility/cardPayments";

const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";

const ConnectPanel = ({ organizationId, showOnboarding, onExited }) => {
  const [loadError, setLoadError] = useState("");
  const [authHint, setAuthHint] = useState(false);

  const fetchClientSecret = useCallback(async () => {
    try {
      const data = await refreshCardPaymentsSession(organizationId);
      return data.clientSecret;
    } catch (error) {
      setLoadError(error.message || "Could not start Stripe.");
      return undefined;
    }
  }, [organizationId]);

  const stripeConnectInstance = useMemo(() => {
    if (!publishableKey) return null;
    return loadConnectAndInitialize({
      publishableKey,
      fetchClientSecret,
      appearance: {
        overlays: "dialog",
        variables: {
          colorPrimary: "#6B5300",
          colorBackground: "#ffffff",
          colorText: "#242424",
          colorSecondaryText: "#4F4F4F",
          borderRadius: "8px",
          buttonPrimaryColorBackground: "#FFD700",
          buttonPrimaryColorBorder: "#FFD700",
          buttonPrimaryColorText: "#242424",
          actionPrimaryColorText: "#6B5300",
          formAccentColor: "#6B5300",
          formHighlightColorBorder: "#6B5300",
        },
      },
    });
  }, [fetchClientSecret]);

  if (!publishableKey) {
    return (
      <p className="text-sm text-brand-muted">
        Card payments is not configured on this site.
      </p>
    );
  }

  if (!stripeConnectInstance) return null;

  return (
    <div className="space-y-4">
      {loadError ? (
        <p className="text-sm text-red-800">{loadError}</p>
      ) : null}
      <h2 className="font-display font-bold text-lg text-brand-charcoal tracking-tight">
        Stripe account details
      </h2>
      {authHint ? (
        <p className="text-sm text-brand-muted">
          If a window doesn’t appear, allow pop-ups and click Confirm again. If
          it stays on a loading page, close it and click Add information
          again.
        </p>
      ) : null}
      <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
        {showOnboarding ? (
          <ConnectAccountOnboarding
            onExit={onExited}
            onLoadError={(event) => {
              const message =
                event?.error?.message ||
                event?.error ||
                "Could not load Stripe.";
              setLoadError(
                typeof message === "string" ? message : "Could not load Stripe."
              );
            }}
            onStepChange={({ step }) => {
              if (step === "stripe_user_authentication") setAuthHint(true);
            }}
          />
        ) : (
          <>
            <ConnectNotificationBanner />
            <ConnectAccountManagement />
          </>
        )}
      </ConnectComponentsProvider>
    </div>
  );
};

export default ConnectPanel;
